/**
 * CSO 驾驶舱 · externalMetrics 解析工具
 *
 * 后端 externalMetrics.data 一次性返回整个指标区四块的数据，
 * 用 region_code 区分区块（0=核心经营指标 / 1=采购成本 / 2=核心KPI / 3=战略推进），
 * 块内用 sort_index 排序，每条记录带 meta_json（含 mom 环比）。
 *
 * 各组件的「结构/判定/格式化」逻辑统一收敛到这里，组件只负责展示。
 */

// region_code → 语义（仅作文档，页面分区固定 0~3）
export const REGION = {
  CORE_INDICATORS: 0, // 核心经营指标
  PROCUREMENT_COST: 1, // 采购成本分析
  CORE_KPI: 2, // 核心 KPI
  STRATEGY_PROGRESS: 3 // 战略推进
}

// direction → 展示
const DIRECTION_META = {
  up: { icon: '↑', cls: 'trend-up' },
  down: { icon: '↓', cls: 'trend-down' },
  flat: { icon: '→', cls: 'trend-flat' }
}
const FALLBACK_DIRECTION = 'flat'

/**
 * 解析 meta_json 字符串（JSON.parse 失败按无环比处理，不抛错）。
 * 返回：
 *  {
 *    isPoint: Boolean,            // 无环比（type:point 或 meta 缺失）
 *    mom: { delta_txt, direction, delta_pct, delta } | null,
 *    item_group: String|undefined // 采购成本拆分条的分类名（如"集成电路"）
 *  }
 */
export function parseMeta(metaJson) {
  let meta = {}
  if (typeof metaJson === 'string') {
    try {
      meta = metaJson ? JSON.parse(metaJson) : {}
    } catch (e) {
      meta = {}
    }
  } else if (metaJson && typeof metaJson === 'object') {
    meta = metaJson
  }
  const mom = meta.mom
  if (!mom || typeof mom !== 'object') {
    return { isPoint: true, mom: null, item_group: meta.item_group }
  }
  return {
    isPoint: false,
    mom: {
      delta_txt: mom.delta_txt,
      direction: DIRECTION_META[mom.direction] ? mom.direction : FALLBACK_DIRECTION,
      delta_pct: mom.delta_pct,
      delta: mom.delta
    },
    item_group: meta.item_group
  }
}

/**
 * 一条记录是否「有有效数据」。
 * metric_value 为空串 / null / undefined / NaN → 无效（该卡隐藏）。
 * 注意：数值 0 是合法业务值，不算空。
 */
export function isValidMetric(item) {
  if (!item) return false
  const v = item.metric_value
  if (v === undefined || v === null || v === '') return false
  return Number.isFinite(Number(v))
}

/**
 * 按 region_code 分组，组内按 sort_index 升序。
 * 返回 { [region_code]: [record, ...] }，仅包含有效记录。
 */
export function groupByRegion(data) {
  const groups = {}
  if (!Array.isArray(data)) return groups
  data.forEach((item) => {
    if (!isValidMetric(item)) return
    const code = item.region_code
    if (!groups[code]) groups[code] = []
    groups[code].push(item)
  })
  Object.keys(groups).forEach((code) => {
    groups[code].sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0))
  })
  return groups
}

/** 千分位数字字符串；非法值返回 '0' */
export function numStr(val) {
  const num = Number(val)
  return Number.isFinite(num) ? num.toLocaleString() : '0'
}

/** 金额（元）转「万」字符串，最多 2 位小数、去尾随 0；非法值返回 '0' */
export function wanStr(val) {
  const num = Number(val)
  if (!Number.isFinite(num)) return '0'
  const wan = num / 10000
  const display = Number.isInteger(wan) ? String(wan) : wan.toFixed(2).replace(/\.?0+$/, '')
  return display.toLocaleString()
}

/** 金额（元）转「万」字符串，固定 2 位小数；非法值返回 '0.00' */
export function wanStrFixed(val) {
  const num = Number(val)
  if (!Number.isFinite(num)) return '0.00'
  return (num / 10000).toFixed(2).toLocaleString()
}

/**
 * 金额类环比文案：从 delta_txt 提取括号内相对百分比，去掉冗长的 ¥ 金额前缀（避免挤占指标名）。
 * 例："+¥291150 (+22.5%)" → "+22.5%"；"-¥27296 (-9.7%)" → "-9.7%"；非法/无括号返回原样。
 * 后缀 % 取自后端已拼好的 delta_txt，语义无歧义（金额类对应相对百分比）。
 */
export function moneyTrendText(deltaTxt) {
  const m = /\(([+-]?\d+(?:\.\d+)?%)\)/.exec(String(deltaTxt || ''))
  if (!m) return deltaTxt || ''
  // 括号内已自带符号（如 (+22.5%) / (-9.7%)），直接返回即可
  return m[1]
}

/**
 * 环比展示：direction 决定箭头与颜色，文案直接用后端 delta_txt。
 * 返回 { value, cls, icon }；无环比时 value=''、icon=''。
 */
export function trendOf(meta) {
  const isEmpty = !meta || meta.isPoint || !meta.mom
  if (isEmpty) {
    return { value: '', cls: 'trend-flat', icon: '' }
  }
  const dirMeta = DIRECTION_META[meta.mom.direction] || DIRECTION_META[FALLBACK_DIRECTION]
  return {
    value: meta.mom.delta_txt || '',
    cls: dirMeta.cls,
    icon: dirMeta.icon
  }
}

/**
 * 单条记录 → 通用卡片结构（供核心指标 / KPI 等数值卡复用）。
 *  {
 *    name, rawValue, value, unit,
 *    trendValue, trendClass, trendIcon,
 *    meta           // parseMeta 结果
 *  }
 */
export function parseIndicator(item) {
  const meta = parseMeta(item.meta_json)
  const trend = trendOf(meta)
  return {
    name: item.display_name || '',
    rawValue: item.metric_value,
    value: numStr(item.metric_value),
    unit: item.unit_code || '',
    trendValue: trend.value,
    trendClass: trend.cls,
    trendIcon: trend.icon,
    meta
  }
}
